import fragmentShaderSource from './shaders/fragmentShader.glsl?raw';
import vertexShaderSource from './shaders/vertexShader.glsl?raw';

export class ScreenshotCapture {
  private static instance: ScreenshotCapture | null = null;
  private canvas: HTMLCanvasElement;
  private readonly width: number = 420;
  private readonly height: number = 225;
  private gl: WebGLRenderingContext | null;
  private program: WebGLProgram | null;
  private texture: WebGLTexture | null;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.gl = this.canvas.getContext('webgl');
    this.program = null;
    this.texture = null;
    this.initializeGL();
  }

  public static getInstance(): ScreenshotCapture {
    if (!this.instance) {
      this.instance = new ScreenshotCapture();
    }
    return this.instance;
  }

  private initializeGL(): void {
    if (!this.gl) {
      throw new Error('>>>WebGL context is not available.');
    }

    const vertexShader = this.gl.createShader(this.gl.VERTEX_SHADER);
    const fragmentShader = this.gl.createShader(this.gl.FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) {
      return console.error('>>>Failed to create shaders.');
    }

    this.gl.shaderSource(vertexShader, vertexShaderSource);
    this.gl.shaderSource(fragmentShader, fragmentShaderSource);

    this.gl.compileShader(vertexShader);
    this.gl.compileShader(fragmentShader);

    if (
      !this.gl.getShaderParameter(vertexShader, this.gl.COMPILE_STATUS) ||
      !this.gl.getShaderParameter(fragmentShader, this.gl.COMPILE_STATUS)
    ) {
      console.error('>>>Shader compilation failed.');
    }

    this.program = this.gl.createProgram();

    if (!this.program) {
      return console.error('>>>Failed to create shader program.');
    }

    this.gl.attachShader(this.program, vertexShader);
    this.gl.attachShader(this.program, fragmentShader);
    this.gl.linkProgram(this.program);

    this.texture = this.gl.createTexture();
  }

  public makeScreenshot(video: HTMLVideoElement): Promise<string> {
    return new Promise((resolve) => {
      if (!this.gl || !this.program || !this.texture) {
        throw new Error('WebGL context is not available.');
      }

      if (!this.gl.getProgramParameter(this.program, this.gl.LINK_STATUS)) {
        throw new Error('>>>Shader program linking failed.');
      }

      this.gl.useProgram(this.program);

      const positionAttribute = this.gl.getAttribLocation(this.program, 'a_position');
      const videoTextureLocation = this.gl.getUniformLocation(this.program, 'u_videoTexture');

      const positionBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, positionBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), this.gl.STATIC_DRAW);
      this.gl.enableVertexAttribArray(positionAttribute);

      this.gl.vertexAttribPointer(positionAttribute, 2, this.gl.FLOAT, false, 0, 0);

      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, video);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

      this.gl.uniform1i(videoTextureLocation, 0);

      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);

      this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

      const pixels = new Uint8Array(this.width * this.height * 4);
      this.gl.readPixels(0, 0, this.width, this.height, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixels);

      resolve(this.canvas.toDataURL('image/jpeg', 0.7));
    });
  }
}
